import config from "appConfig";

const theory = `<h3 class=sectionHead>Introduction</h3>
<p>
   The acoustic specific impedance is the ratio of the complex pressure amplitude
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>p</mi>
      <mo class=MathClass-open>(</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   to the complex velocity amplitude
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>v</mi>
      <mo class=MathClass-open>(</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   by analogy with the electrical impedance which is the ratio of a voltage to a current. We have already seen that the impedance of a progressive wave is
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mo class=MathClass-bin>±</mo>
   </math>
   the characteristic impedance i.e.
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>ρ</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
      <mi>c</mi>
   </math>. This characteristic impedance is real and independent of the position and frequency. In general, the impedance is a complex quantity which depends on the frequency. The concept of impedance then make sense only in the frequency domain (as in electricity). As it is independent of the amplitude of the wave, the impedance considered at the input of a tube can be seen as the response of the tube to a given excitation.
<h3 class=sectionHead>Impedance and reflection coefficient</h3>
<p>The reflective properties of a wall at normal incidence can be characterized by its reflection coefficient or its impedance. Indeed, the pressure can be written
<table class=equation>
   <tr>
      <td style="font-size: 11px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi>p</mi>
            <mo class=MathClass-open>(</mo>
            <mi>ω</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mi>i</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open>(</mo>
            <mi>ω</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-bin>+</mo>
            <msub>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mi>r</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open>(</mo>
            <mi>ω</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <msup>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>+</mo>
               </mrow>
            </msup>
            <mo class=MathClass-open>(</mo>
            <mi>ω</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-bin>+</mo>
            <msup>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
               </mrow>
            </msup>
            <mo class=MathClass-open>(</mo>
            <mi>ω</mi>
            <mo class=MathClass-close>)</mo>
         </math>
      <td class=eq-no>(1)
</table>
<p>Similarly, the particle velocity can be written
<table class=equation>
   <tr>
      <td style="font-size: 10px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi>v</mi>
            <mo class=MathClass-open>(</mo>
            <mi>ω</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>v</mi>
               </mrow>
               <mrow>
                  <mi>i</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open>(</mo>
            <mi>ω</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-bin>+</mo>
            <msub>
               <mrow>
                  <mi>v</mi>
               </mrow>
               <mrow>
                  <mi>r</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open>(</mo>
            <mi>ω</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-bin>+</mo>
                     </mrow>
                  </msup>
                  <mo class=MathClass-open>(</mo>
                  <mi>ω</mi>
                  <mo class=MathClass-close>)</mo>
                  <mo class=MathClass-bin>−</mo>
                  <msup>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-bin>−</mo>
                     </mrow>
                  </msup>
                  <mo class=MathClass-open>(</mo>
                  <mi>ω</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <mi>c</mi>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(2)
</table>
<p>
   In this last equation
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mo class=MathClass-bin>+</mo>
   </math>
   is for an incident wave coming from the left and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mo class=MathClass-bin>−</mo>
   </math> for an incident wave coming from the right. So
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi>Z</mi>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-bin>+</mo>
                     </mrow>
                  </msup>
                  <mo class=MathClass-bin>+</mo>
                  <msup>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-bin>−</mo>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>v</mi>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-bin>+</mo>
                     </mrow>
                  </msup>
                  <mo class=MathClass-bin>+</mo>
                  <msup>
                     <mrow>
                        <mi>v</mi>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-bin>−</mo>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-bin>+</mo>
                     </mrow>
                  </msup>
                  <mo class=MathClass-bin>+</mo>
                  <msup>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-bin>−</mo>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <mfrac>
                     <mrow>
                        <msup>
                           <mrow>
                              <mi>p</mi>
                           </mrow>
                           <mrow>
                              <mo class=MathClass-bin>+</mo>
                           </mrow>
                        </msup>
                        <mo class=MathClass-bin>−</mo>
                        <msup>
                           <mrow>
                              <mi>p</mi>
                           </mrow>
                           <mrow>
                              <mo class=MathClass-bin>−</mo>
                           </mrow>
                        </msup>
                     </mrow>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>ρ</mi>
                           </mrow>
                           <mrow>
                              <mn>0</mn>
                           </mrow>
                        </msub>
                        <mi>c</mi>
                     </mrow>
                  </mfrac>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(3)
</table>
<p>
   Considering
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msup>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
         </mrow>
      </msup>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>p</mi>
         </mrow>
      </msub>
      <msup>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>+</mo>
         </mrow>
      </msup>
   </math>
   or
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msup>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>+</mo>
         </mrow>
      </msup>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>p</mi>
         </mrow>
      </msub>
      <msup>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
         </mrow>
      </msup>
   </math>
   this leads to
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi>Z</mi>
            <mo class=MathClass-rel>=</mo>
            <mo class=MathClass-bin>±</mo>
            <msub>
               <mrow>
                  <mi>ρ</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mi>c</mi>
            <mfrac>
               <mrow>
                  <mn>1</mn>
                  <mo class=MathClass-bin>+</mo>
                  <msub>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <mn>1</mn>
                  <mo class=MathClass-bin>−</mo>
                  <msub>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(4)
</table>
<p>
   In this last equation
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mo class=MathClass-bin>+</mo>
   </math>
   is for an incident wave coming from the left and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mo class=MathClass-bin>−</mo>
   </math>
   for an incident wave coming from the right. Arbitrarily the definition with a + is used for the impedance of an obstacle: the impedance of an obstacle thus has a positive real part by definition. So, the impedance of the obstacle differs from the impedance of the wave by a factor
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mo class=MathClass-bin>−</mo>
      <mn>1</mn>
   </math>
   for a wave coming from the right.
<p>Note the similarity of this formula with that of the Sound Wave Ratio
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi mathvariant=italic>SWR</mi>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
                  <mo class=MathClass-bin>+</mo>
                  <mo class=MathClass-rel>|</mo>
                  <msub>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-rel>|</mo>
               </mrow>
               <mrow>
                  <mn>1</mn>
                  <mo class=MathClass-bin>−</mo>
                  <mo class=MathClass-rel>|</mo>
                  <msub>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-rel>|</mo>
               </mrow>
            </mfrac>
            <mo class=MathClass-punc>,</mo>
         </math>
      <td class=eq-no>(5)
</table>
<p>
   while noting the fundamental difference between the two formulas:
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant=italic>SWR</mi>
   </math>
   is real and is calculated from the modulus of the reflection coefficient when the impedance is complex and is calculated from the reflection coefficient itself. Thus in the case where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>p</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mo class=MathClass-bin>−</mo>
      <mn>1</mn>
   </math>
   we have
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>Z</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
   </math>
   whereas
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant=italic>SWR</mi>
      <mo class=MathClass-rel>=</mo>
      <mo class=MathClass-bin>+</mo>
      <mi>∞</mi>
   </math>. However if
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>p</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mn>1</mn>
   </math>,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>Z</mi>
      <mo class=MathClass-rel>=</mo>
      <mi>∞</mi>
   </math>
   as well as
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant=italic>SWR</mi>
      <mo class=MathClass-rel>=</mo>
      <mo class=MathClass-bin>+</mo>
      <mi>∞</mi>
   </math>. Also, if
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>Z</mi>
      <mo class=MathClass-bin>∕</mo>
      <msub>
         <mrow>
            <mi>ρ</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
      <mi>c</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>1</mn>
   </math>,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant=italic>SWR</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>1</mn>
   </math>
   as well. Indeed
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant=italic>SWR</mi>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>Z</mi>
      <mo class=MathClass-bin>∕</mo>
      <msub>
         <mrow>
            <mi>ρ</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
      <mi>c</mi>
   </math>
   are equal when
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>Z</mi>
   </math>
   is real and positive. It must be understood that the reflection coefficient
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>p</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   and the impedance
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>Z</mi>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   are local quantities depending on the considered point, while the  <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant=italic>SWR</mi>
   </math> is a global quantity valid for any value of
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>x</mi>
   </math>
   as long as damping is not considered.
<p>The following relation for the reflection coefficient versus the impedance is also useful
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msub>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mi>p</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi>Z</mi>
                  <mo class=MathClass-bin>−</mo>
                  <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <mi>c</mi>
               </mrow>
               <mrow>
                  <mi>Z</mi>
                  <mo class=MathClass-bin>+</mo>
                  <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <mi>c</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi>Z</mi>
                  <mo class=MathClass-bin>−</mo>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mi>c</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <mi>Z</mi>
                  <mo class=MathClass-bin>+</mo>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mi>c</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-punc>,</mo>
         </math>
      <td class=eq-no>(6)
</table>
<p>
   where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>c</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>ρ</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
      <mi>c</mi>
   </math>
   here but the last expression is valid in many other contexts.
<h3 class=sectionHead>Impedance of a standing wave</h3>
<p>
   For a standing wave, the impedance
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>Z</mi>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <mi>p</mi>
            <mo class=MathClass-open>(</mo>
            <mi>x</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>ω</mi>
            <mo class=MathClass-close>)</mo>
         </mrow>
         <mrow>
            <mi>v</mi>
            <mo class=MathClass-open>(</mo>
            <mi>x</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>ω</mi>
            <mo class=MathClass-close>)</mo>
         </mrow>
      </mfrac>
   </math>
   depends on both the position and frequency. Indeed, since the complex amplitude of the pressure is given by
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>p</mi>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mn>2</mn>
      <mo class=MathClass-rel>|</mo>
      <mi>A</mi>
      <mo class=MathClass-rel>|</mo>
      <msup>
         <mrow>
            <mi>e</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>jϕ</mi>
         </mrow>
      </msup>
      <mi class=qopname>cos</mi>
      <mo>⁡</mo>
      <mo class=MathClass-open>(</mo>
      <mi mathvariant=italic>kx</mi>
      <mo class=MathClass-bin>+</mo>
      <mi mathvariant=italic>Δϕ</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   and the complex amplitude of the particle velocity is given by
   <span style="font-size: 15px">
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>v</mi>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mo class=MathClass-bin>−</mo>
      <mn>2</mn>
      <mi>j</mi>
      <mfrac>
         <mrow>
            <mo class=MathClass-rel>|</mo>
            <mi>A</mi>
            <mo class=MathClass-rel>|</mo>
         </mrow>
         <mrow>
            <msub>
               <mrow>
                  <mi>ρ</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mi>c</mi>
         </mrow>
      </mfrac>
      <msup>
         <mrow>
            <mi>e</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>jϕ</mi>
         </mrow>
      </msup>
      <mi class=qopname>sin</mi>
      <mo>⁡</mo>
      <mo class=MathClass-open>(</mo>
      <mi mathvariant=italic>kx</mi>
      <mo class=MathClass-bin>+</mo>
      <mi mathvariant=italic>Δϕ</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   </span>
   the impedance of the standing wave is given by
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi>Z</mi>
            <mo class=MathClass-open>(</mo>
            <mi>x</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>ω</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mi>j</mi>
            <msub>
               <mrow>
                  <mi>ρ</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mi>c</mi>
            <mi class=qopname>cot</mi>
            <mo>⁡</mo>
            <mo class=MathClass-open>(</mo>
            <mi mathvariant=italic>kx</mi>
            <mo class=MathClass-bin>+</mo>
            <mi mathvariant=italic>Δϕ</mi>
            <mo class=MathClass-close>)</mo>
         </math>
      <td class=eq-no>(7)
</table>
<p>which can as well be written
<table class=equation>
   <tr>
      <td style="font-size: 12px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi>Z</mi>
            <mo class=MathClass-open>(</mo>
            <mi>x</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>ω</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mo class=MathClass-bin>−</mo>
            <mi>j</mi>
            <msub>
               <mrow>
                  <mi>ρ</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mi>c</mi>
            <mi class=qopname>tan</mi>
            <mo>⁡</mo>
            <mo class=MathClass-open>(</mo>
            <mi mathvariant=italic>kx</mi>
            <mo class=MathClass-bin>+</mo>
            <mi mathvariant=italic>Δϕ</mi>
            <mo class=MathClass-bin>+</mo>
            <mi>π</mi>
            <mo class=MathClass-bin>∕</mo>
            <mn>2</mn>
            <mo class=MathClass-close>)</mo>
         </math>
      <td class=eq-no>(8)
</table>
<p>
   This equation is still valid considering thermo-viscous wall losses. In that case
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>k</mi>
   </math>
   is a complex number.
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/01/07/theory/figure-1.svg alt="Figure 1. Input impedance modulus of a close tube of lengh <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mn>50</mn><mstyle class=text><mtext>cm</mtext></mstyle></math> and radius <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mn>1</mn><mstyle class=text><mtext>cm</mtext></mstyle></math> with thermoviscous losses (thick line) and without losses (thin line)."><figcaption class=caption>Figure 1. Input impedance modulus of a close tube of lengh <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mn>50</mn><mstyle class=text><mtext>cm</mtext></mstyle></math> and radius <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mn>1</mn><mstyle class=text><mtext>cm</mtext></mstyle></math> with thermoviscous losses (thick line) and without losses (thin line).</figcaption></figure>
<p>
   It is found that the consideration of losses allows a finite value of the impedance. Indeed, considering
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>k</mi>
      <mo class=MathClass-rel>=</mo>
      <mi>ℜ</mi>
      <mo class=MathClass-open>(</mo>
      <mi>k</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-bin>−</mo>
      <mi mathvariant=italic>jα</mi>
   </math>
   with
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>α</mi>
   </math>
   the absorption coefficient and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>ℜ</mi>
      <mo class=MathClass-open>(</mo>
      <mi mathvariant=italic>kx</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-bin>+</mo>
      <mi mathvariant=italic>Δϕ</mi>
      <mo class=MathClass-rel>=</mo>
      <mi mathvariant=italic>nπ</mi>
   </math>, it can be shown that
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>Z</mi>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>ρ</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
      <mi>c</mi>
      <mi class=qopname>coth</mi>
      <mo>⁡</mo>
      <mo class=MathClass-open>(</mo>
      <mi mathvariant=italic>αx</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>≈</mo>
      <mfrac>
         <mrow>
            <msub>
               <mrow>
                  <mi>ρ</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mi>c</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>αx</mi>
         </mrow>
      </mfrac>
   </math>. As well if
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>ℜ</mi>
      <mo class=MathClass-open>(</mo>
      <mi mathvariant=italic>kx</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-bin>+</mo>
      <mi mathvariant=italic>Δϕ</mi>
      <mo class=MathClass-rel>=</mo>
      <mi mathvariant=italic>nπ</mi>
      <mo class=MathClass-bin>+</mo>
      <mi>π</mi>
      <mo class=MathClass-bin>∕</mo>
      <mn>2</mn>
   </math>,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>Z</mi>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>ρ</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
      <mi>c</mi>
      <mi class=qopname>tanh</mi>
      <mo>⁡</mo>
      <mo class=MathClass-open>(</mo>
      <mi mathvariant=italic>αx</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>≈</mo>
      <msub>
         <mrow>
            <mi>ρ</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
      <mi mathvariant=italic>cαx</mi>
   </math>. It is interesting to note that the influence of losses is negligible except in the vicinity of the natural frequencies (see <a href="#" data-img-alt="Figure 1. Reflection and transmission of waves at an interface between two media." data-img-src=${config.ASSETS_URL}/01/07/theory/figure-1.svg>Figure 1</a>). So, in practice we note that it is often possible to neglect the losses if not close to a natural frequency of the studied system. Note also the slight shift of the peaks between the lossless case and the case with losses. This discrepancy is due to the dispersion phenomenon.
<h3 class=sectionHead>Impedance for a plane wave in general</h3>
<p>
   In general
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>p</mi>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mi>A</mi>
      <msup>
         <mrow>
            <mi>e</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
            <mi mathvariant=italic>jkx</mi>
         </mrow>
      </msup>
      <mo class=MathClass-bin>+</mo>
      <mi>B</mi>
      <msup>
         <mrow>
            <mi>e</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>+</mo>
            <mi mathvariant=italic>jkx</mi>
         </mrow>
      </msup>
      </math>
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mo class=MathClass-rel>=</mo>
      <msubsup>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>+</mo>
         </mrow>
      </msubsup>
      <msup>
         <mrow>
            <mi>e</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
            <mi mathvariant=italic>jkx</mi>
         </mrow>
      </msup>
      <mo class=MathClass-bin>+</mo>
      <msubsup>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
         </mrow>
      </msubsup>
      <msup>
         <mrow>
            <mi>e</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>+</mo>
            <mi mathvariant=italic>jkx</mi>
         </mrow>
      </msup>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>v</mi>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <mi>A</mi>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
                  <mi mathvariant=italic>jkx</mi>
               </mrow>
            </msup>
            <mo class=MathClass-bin>−</mo>
            <mi>B</mi>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>+</mo>
                  <mi mathvariant=italic>jkx</mi>
               </mrow>
            </msup>
         </mrow>
         <mrow>
            <msub>
               <mrow>
                  <mi>ρ</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mi>c</mi>
         </mrow>
      </mfrac>
      </math>
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <msubsup>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>+</mo>
               </mrow>
            </msubsup>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
                  <mi mathvariant=italic>jkx</mi>
               </mrow>
            </msup>
            <mo class=MathClass-bin>+</mo>
            <msubsup>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
               </mrow>
            </msubsup>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>+</mo>
                  <mi mathvariant=italic>jkx</mi>
               </mrow>
            </msup>
         </mrow>
         <mrow>
            <msub>
               <mrow>
                  <mi>ρ</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mi>c</mi>
         </mrow>
      </mfrac>
   </math>
   subscript 0 being used for
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>x</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
   </math>. So
<table class=equation>
   <tr>
      <td style="font-size: 14px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi>Z</mi>
            <mo class=MathClass-open>(</mo>
            <mi>x</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>ρ</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mi>c</mi>
            <mfrac>
               <mrow>
                  <msubsup>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-bin>+</mo>
                     </mrow>
                  </msubsup>
                  <msup>
                     <mrow>
                        <mi>e</mi>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-bin>−</mo>
                        <mi mathvariant=italic>jkx</mi>
                     </mrow>
                  </msup>
                  <mo class=MathClass-bin>+</mo>
                  <msubsup>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-bin>−</mo>
                     </mrow>
                  </msubsup>
                  <msup>
                     <mrow>
                        <mi>e</mi>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-bin>+</mo>
                        <mi mathvariant=italic>jkx</mi>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <msubsup>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-bin>+</mo>
                     </mrow>
                  </msubsup>
                  <msup>
                     <mrow>
                        <mi>e</mi>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-bin>−</mo>
                        <mi mathvariant=italic>jkx</mi>
                     </mrow>
                  </msup>
                  <mo class=MathClass-bin>−</mo>
                  <msubsup>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-bin>−</mo>
                     </mrow>
                  </msubsup>
                  <msup>
                     <mrow>
                        <mi>e</mi>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-bin>+</mo>
                        <mi mathvariant=italic>jkx</mi>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(9)
</table>
<p>
   Considering that
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msubsup>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>+</mo>
         </mrow>
      </msubsup>
      <mo class=MathClass-bin>+</mo>
      <msubsup>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
         </mrow>
      </msubsup>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>ρ</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
      <mi>c</mi>
      <msub>
         <mrow>
            <mi>v</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msubsup>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>+</mo>
         </mrow>
      </msubsup>
      <mo class=MathClass-bin>−</mo>
      <msubsup>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
         </mrow>
      </msubsup>
   </math>, we can write
<table class=equation>
   <tr>
      <td style="font-size: 12px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi>Z</mi>
            <mo class=MathClass-open>(</mo>
            <mi>x</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <mi class=qopname>cos</mi>
                  <mo>⁡</mo>
                  <mi mathvariant=italic>kx</mi>
                  <mo class=MathClass-bin>−</mo>
                  <mi>j</mi>
                  <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <mi>c</mi>
                  <mi class=qopname>sin</mi>
                  <mo>⁡</mo>
                  <mi mathvariant=italic>kx</mi>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>v</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <mi class=qopname>cos</mi>
                  <mo>⁡</mo>
                  <mi mathvariant=italic>kx</mi>
                  <mo class=MathClass-bin>−</mo>
                  <mi>j</mi>
                  <msub>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <mi mathvariant=italic>sinkx</mi>
                  <mo class=MathClass-bin>∕</mo>
                  <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <mi>c</mi>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(10)
</table>
<p>
   Moreover, considering that
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
      <mo class=MathClass-bin>∕</mo>
      <msub>
         <mrow>
            <mi>v</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
   and that
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>ρ</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
      <mi>c</mi>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>c</mi>
         </mrow>
      </msub>
   </math>
   this leads to
<table class=equation>
   <tr>
      <td style="font-size: 14px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi>Z</mi>
            <mo class=MathClass-open>(</mo>
            <mi>x</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <mo class=MathClass-bin>−</mo>
                  <mi>j</mi>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mi>c</mi>
                     </mrow>
                  </msub>
                  <mi class=qopname>tan</mi>
                  <mo>⁡</mo>
                  <mo class=MathClass-open>(</mo>
                  <mi mathvariant=italic>kx</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mrow>
                  <mn>1</mn>
                  <mo class=MathClass-bin>−</mo>
                  <mi>j</mi>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <mi class=qopname>tan</mi>
                  <mo>⁡</mo>
                  <mo class=MathClass-open>(</mo>
                  <mi mathvariant=italic>kx</mi>
                  <mo class=MathClass-close>)</mo>
                  <mo class=MathClass-bin>∕</mo>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mi>c</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(11)
</table>
<p>or alternatively
<table class=equation>
   <tr>
      <td style="font-size: 15px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msub>
               <mrow>
                  <mi>Z</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi>Z</mi>
                  <mo class=MathClass-open>(</mo>
                  <mi>x</mi>
                  <mo class=MathClass-close>)</mo>
                  <mo class=MathClass-bin>+</mo>
                  <mi>j</mi>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mi>c</mi>
                     </mrow>
                  </msub>
                  <mi class=qopname>tan</mi>
                  <mo>⁡</mo>
                  <mo class=MathClass-open>(</mo>
                  <mi mathvariant=italic>kx</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mrow>
                  <mn>1</mn>
                  <mo class=MathClass-bin>+</mo>
                  <mi mathvariant=italic>jZ</mi>
                  <mo class=MathClass-open>(</mo>
                  <mi>x</mi>
                  <mo class=MathClass-close>)</mo>
                  <mi class=qopname>tan</mi>
                  <mo>⁡</mo>
                  <mo class=MathClass-open>(</mo>
                  <mi mathvariant=italic>kx</mi>
                  <mo class=MathClass-close>)</mo>
                  <mo class=MathClass-bin>∕</mo>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mi>c</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(12)
</table>
<p>This equation allows for example the calculation of the input impedance of a tube knowing its termination impedance.
<h3 class=sectionHead>References</h3>
<dl class=thebibliography>
  <dt>[1]
  <dd>L.J. Beranek. Acoustics. The American Institute of Physics, New York, 1986.
  <dt>[2]
  <dd>A.D. Pierce. Acoustics: an Introduction to its Physical Principles and Applications. MCGraw-Hill Book Company, Inc., New York, 1981.
  <dt>[3]
  <dd>M. Rossi.  ́Electroacoustique. Presses Polytechniques Romandes, Lausanne, 1986.
  <dt>[4]
  <dd>S. Temkin. Elements of Acoustics. Wiley, New York, 1981.
</dl>`;

export default theory;
